import React, {useState, useCallback, useRef} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {Link} from '@modifi/router'
import {DropDownArrowIcon} from '@modifi/ui'
import {KEY_JANUS, useTranslation} from '@modifi/plugin-i18n'

import {iconMap} from '../../../../../constants/websiteRevampIconMap'
import {
  ActualItem,
  LightStrong,
  NavTitleWrapper,
  SecondaryNavList,
  DummyIcon,
  NavigationEntryWrapper,
} from './styles'

const entryPropShape = {
  type: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  items: PropTypes.array,
}
entryPropShape.items = PropTypes.arrayOf(PropTypes.shape(entryPropShape).isRequired)

const propTypes = {
  entry: PropTypes.shape(entryPropShape).isRequired,
  collapsable: PropTypes.bool,
  forceIcons: PropTypes.bool,
  handleNavItemClick: PropTypes.func,
  hideSubMenu: PropTypes.bool,
}
const defaultProps = {
  collapsable: true,
  forceIcons: false,
  handleNavItemClick: () => null,
  hideSubMenu: false,
}

export type NavigationEntryProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const NavigationEntry: React.FC<NavigationEntryProps> = ({
  entry,
  collapsable,
  forceIcons,
  handleNavItemClick,
  hideSubMenu,
}) => {
  const {t} = useTranslation(KEY_JANUS)
  const [subNavigationSize, setSubNavigationSize] = useState(0)

  const subNavRef = useRef<HTMLUListElement>(null)
  const handleMouseOver = useCallback(() => {
    if (subNavRef.current) {
      const childHeight = Array.from(subNavRef.current.children).reduce(
        (sum, el) => sum + el.clientHeight,
        0
      )
      setSubNavigationSize(childHeight)
    }
  }, [])

  let IconComponent = entry?.icon ? iconMap[entry.icon] : null
  if (!IconComponent && forceIcons) IconComponent = DummyIcon

  let item = null

  if (hideSubMenu && entry?.items?.length) {
    const updatedEntryPath = entry?.items[0]?.path
    const isExternalLink = entry?.items[0]?.type === 'link'
    item = (
      <Link
        to={updatedEntryPath}
        isExternal={isExternalLink}
        targetBlank={isExternalLink}
        scrollToTop
        onClick={handleNavItemClick}
      >
        <ActualItem>
          {/* {IconComponent && <IconComponent height={32} width={32} style={{marginRight: '16px'}} />} */}
          <span className="primary-nav-label" title={entry.label ? t(entry.label) : undefined}>
            {t(entry.label || '')}
          </span>
        </ActualItem>
      </Link>
    )
  } else if (entry?.path) {
    const isExternalLink = entry?.type === 'link'
    item = (
      <Link
        to={entry?.path}
        isExternal={isExternalLink}
        targetBlank={isExternalLink}
        scrollToTop
        onClick={handleNavItemClick}
      >
        <ActualItem>
          {IconComponent && <IconComponent height={32} width={32} style={{marginRight: '16px'}} />}
          <span>
            <LightStrong>{t(entry.label || '')}</LightStrong>
            {entry?.description && (
              <>
                <br />
                {t(entry.description)}
              </>
            )}
          </span>
          {collapsable && entry.items && (
            <>
              &nbsp; <DropDownArrowIcon />
            </>
          )}
        </ActualItem>
      </Link>
    )
  } else {
    item = (
      <ActualItem>
        <span className="primary-nav-label" title={entry.label ? t(entry.label) : undefined}>
          {t(entry.label || '')}
        </span>
        {collapsable && entry.items && (
          <>
            &nbsp; <DropDownArrowIcon />
          </>
        )}
      </ActualItem>
    )
  }

  if (!entry.items || hideSubMenu) {
    return <NavigationEntryWrapper>{item}</NavigationEntryWrapper>
  }

  const anyChildItemHasIcon = entry.items.some(i => !!i.icon)

  return (
    <NavigationEntryWrapper
      className="has-sub-navigation"
      childNavHeight={subNavigationSize}
      collapsable={collapsable}
      onMouseEnter={handleMouseOver}
    >
      <NavTitleWrapper>{item}</NavTitleWrapper>
      <SecondaryNavList ref={subNavRef}>
        {entry.items.map((subEntry, i) => (
          <NavigationEntry
            entry={subEntry}
            // eslint-disable-next-line react/no-array-index-key
            key={subEntry.label ?? i}
            forceIcons={anyChildItemHasIcon}
            collapsable={collapsable}
            handleNavItemClick={handleNavItemClick}
          />
        ))}
      </SecondaryNavList>
    </NavigationEntryWrapper>
  )
}

NavigationEntry.propTypes = propTypes
NavigationEntry.defaultProps = defaultProps

export default React.memo(NavigationEntry)
